import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { breadcrumbSchema, webPageSchema, PROD_ORIGIN, HANNAH_IMAGE } from '../lib/seo';
import { Button } from '../components/ui/button';
import { hannah, hannahPortrait } from '../data/content';

export default function AboutPage() {
  const path = '/about';
  const title = 'About Hannah Hulley — Founder of Founder Growth Collective';
  const description = 'Hannah Hulley is the founder of Founder Growth Collective. 15+ years in B2B sales, venture-backed SaaS leadership and UK sales expansion for funded scale-ups.';

  const bc = [{ name: 'Home', path: '/' }, { name: 'About Hannah', path }];

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${PROD_ORIGIN}/#hannah`,
    name: 'Hannah Hulley',
    jobTitle: 'Founder & CEO',
    image: HANNAH_IMAGE,
    worksFor: { '@id': `${PROD_ORIGIN}/#organization` },
    knowsAbout: [
      'B2B sales',
      'SaaS sales',
      'Startup sales function design',
      'Founder-led sales',
      'Sales team scaling',
      'Sales hiring',
    ],
    description: hannah.bio,
  };

  const schemas = [
    webPageSchema({ path, name: title, description }),
    breadcrumbSchema(bc),
    personSchema,
  ];

  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen">
      <SEO title={title} description={description} path={path} image={HANNAH_IMAGE} schemas={schemas} />
      <Navbar />

      <main className="pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={bc} />

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-[#E3DBD1]">
                <img
                  src={hannahPortrait}
                  alt="Hannah Hulley, Founder of Founder Growth Collective"
                  className="w-full h-full object-cover object-top"
                  width={800}
                  height={1000}
                  loading="eager"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="sec-num text-sm tracking-[0.3em] uppercase mb-4">/ About Hannah</p>
              <h1 className="font-serif text-[44px] lg:text-[64px] leading-[1.02] tracking-[-0.02em] text-[#2E2C28] font-light">
                {hannah.name}<span className="block italic text-[#B5876C] text-2xl lg:text-3xl mt-3">{hannah.role}</span>
              </h1>
              <p className="mt-8 text-lg lg:text-xl leading-[1.65] text-[#4a463f] font-light">
                {hannah.bio}
              </p>

              <div className="mt-12 grid grid-cols-2 gap-8 max-w-lg border-t border-[#d9d2ca] pt-8">
                <div><p className="font-serif text-3xl text-[#2E2C28] font-light">15+</p><p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#7a7369]">Years in B2B sales</p></div>
                <div><p className="font-serif text-3xl text-[#2E2C28] font-light">6 → 50</p><p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#7a7369]">SaaS team scaled</p></div>
              </div>

              <div className="mt-12">
                <h2 className="font-serif text-2xl text-[#2E2C28] font-light mb-4">Explore how Hannah works with founders</h2>
                <ul className="space-y-2 text-base text-[#4a463f]">
                  <li><Link to="/services" className="underline decoration-[#B5876C]/50 underline-offset-4 hover:text-[#B5876C]">Services</Link></li>
                  <li><Link to="/founder-led-sales" className="underline decoration-[#B5876C]/50 underline-offset-4 hover:text-[#B5876C]">Founder-Led Sales</Link></li>
                  <li><Link to="/testimonials" className="underline decoration-[#B5876C]/50 underline-offset-4 hover:text-[#B5876C]">Client testimonials</Link></li>
                </ul>
              </div>

              <div className="mt-12">
                <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase">
                  <Link to="/contact" className="inline-flex items-center gap-3">Book a Discovery Call <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
